/**
 * Modal za promenu tipa korisnika i izabrane organizacije.
 */
import React, { useState, useEffect } from 'react';
import ApiService from '@/core/api/ApiService';
import { API_ENDPOINTS } from '@/core/api/endpoints';
import UserPreferencesService from '../../services/UserPreferencesService';
import './UserSettingsModal.css';

const UserSettingsModal = ({ isOpen, onClose }) => {
  const [userType, setUserType] = useState('individual');
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrganizationId, setSelectedOrganizationId] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingOrgs, setLoadingOrgs] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Učitaj postojeće preferencije kada se modal otvori
  useEffect(() => {
    if (isOpen) {
      const preferences = UserPreferencesService.getPreferences();
      setUserType(preferences.userType || 'individual');
      setSelectedOrganizationId(preferences.selectedOrganizationId || '');
      setError(null);
      setSuccess(false);
      
      // Učitaj organizacije ako je tip organization
      if (preferences.userType === 'organization') {
        loadOrganizations();
      }
    }
  }, [isOpen]);

  // Učitaj organizacije kada korisnik izabere "organization"
  useEffect(() => {
    if (isOpen && userType === 'organization' && organizations.length === 0) {
      loadOrganizations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, userType]);

  const loadOrganizations = async () => {
    setLoadingOrgs(true);
    setError(null);
    
    try {
      const response = await ApiService.get(API_ENDPOINTS.CSO.LIST);
      setOrganizations(response.data || []);
    } catch (err) {
      setError('Грешка при учитавању листе организација. Молимо покушајте поново.');
    } finally {
      setLoadingOrgs(false);
    }
  };

  const handleSave = () => {
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      let preferences = {
        userType: userType,
        selectedOrganizationId: null,
        selectedOrganizationName: null,
      };

      if (userType === 'organization') {
        if (!selectedOrganizationId) {
          setError('Молимо изаберите организацију.');
          setLoading(false);
          return;
        }

        const selectedOrg = organizations.find(org => org.id === selectedOrganizationId || org.id === parseInt(selectedOrganizationId));
        if (!selectedOrg) {
          setError('Изабрана организација није пронађена.');
          setLoading(false);
          return;
        }

        preferences.selectedOrganizationId = selectedOrg.id;
        preferences.selectedOrganizationName = selectedOrg.name || selectedOrg.naziv || 'Непозната организација';
      }

      UserPreferencesService.savePreferences(preferences);
      setSuccess(true);
      
      // Zatvori modal nakon kratke pauze da korisnik vidi poruku o uspehu
      setTimeout(() => {
        onClose();
        // Osveži stranicu da se promene primene
        window.location.reload();
      }, 1000);
    } catch (err) {
      setError('Грешка при чувању подешавања. Молимо покушајте поново.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content user-settings-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Подешавања</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        <div className="modal-body">
          <p className="modal-description">
            Изаберите да ли настављате као физичко лице или као представник организације цивилног друштва.
          </p>

          <div className="user-type-options">
            <div className="user-type-option">
              <input
                type="radio"
                id="settingsUserTypeIndividual"
                name="settingsUserType"
                value="individual"
                checked={userType === 'individual'}
                onChange={(e) => setUserType(e.target.value)}
              />
              <label htmlFor="settingsUserTypeIndividual">
                <span className="option-title">Физичко лице</span>
                <span className="option-description">Настављате као појединац</span>
              </label>
            </div>

            <div className="user-type-option">
              <input
                type="radio"
                id="settingsUserTypeOrganization"
                name="settingsUserType"
                value="organization"
                checked={userType === 'organization'}
                onChange={(e) => {
                  setUserType(e.target.value);
                  if (organizations.length === 0) {
                    loadOrganizations();
                  }
                }}
              />
              <label htmlFor="settingsUserTypeOrganization">
                <span className="option-title">Организација цивилног друштва</span>
                <span className="option-description">Настављате као представник организације</span>
              </label>
            </div>
          </div>

          {userType === 'organization' && (
            <div className="organization-selector">
              <label htmlFor="settingsOrganizationSelect">
                Изаберите организацију <span className="required">*</span>
              </label>
              {loadingOrgs ? (
                <div className="loading-message">Учитавање организација...</div>
              ) : (
                <select
                  id="settingsOrganizationSelect"
                  value={selectedOrganizationId}
                  onChange={(e) => setSelectedOrganizationId(e.target.value)}
                  className="organization-select"
                  required
                >
                  <option value="">-- Изаберите организацију --</option>
                  {organizations.map((org) => (
                    <option key={org.id} value={org.id}>
                      {org.name || org.naziv || `Организација ${org.id}`}
                    </option>
                  ))}
                </select>
              )}
              {organizations.length === 0 && !loadingOrgs && (
                <p className="error-message">
                  Нема доступних организација. Молимо контактирајте администратора.
                </p>
              )}
            </div>
          )}

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          {success && (
            <div className="success-message">
              Подешавања су успешно сачувана!
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onClose}
            disabled={loading}
          >
            Откажи
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSave}
            disabled={loading || (userType === 'organization' && !selectedOrganizationId)}
          >
            {loading ? 'Чување...' : 'Сачувај'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserSettingsModal;

